/**
 * Billing account invoices grid — recurring and one-time invoices for a
 * single billing account, with per-invoice line-item detail, QuickBooks
 * push/association, and bulk "mark complete" for a selected billing period.
 *
 * Server-backed via EntityGrid over the odl-app billing-invoices grid-spec
 * (GET odl-app/billing-invoices/). Recurring vs. one-time is the
 * `is_recurring` filter behind the Recurring/One Time tabs; status and
 * billing-period filters live in the grid toolbar; search runs through
 * EntityGrid's built-in quick filter against the descriptor's searchable
 * `name` field. Expanding a row (DataGridPro master-detail, threaded through
 * via EntityGrid's `gridProps` escape hatch) shows the invoice's
 * category-grouped line items plus the Mark Complete / Push Invoice actions.
 * The billing-period summary rollup stays on the legacy /billing/invoices/
 * endpoint for its bounded aggregate read — a different filter-param shape
 * from the grid's, so it keeps its own params object.
 *
 * Contains:
 * - InvoiceTable — default export, mounted by AccountInvoicesView's Invoices tab
 */
import { TabContext, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { fetchInvoices } from '@sophocles/api/billing';
import { useUser } from '@sophocles/app';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { EntityGrid, MONEY_FORMAT_OPTIONS } from '@sophocles/data-grids';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import {
  markInvoicesCompleteByAccountId,
  markInvoicesCompleteByIds,
  pushQuickBooksInvoice,
} from '@sophocles/features/billing/api';
import { selectBillingAccount } from '@sophocles/features/billing/slice';
import { createQuickBooksAssociation, disassociateQuickBooksCustomer } from '@sophocles/features/quickbooks/api';
import {
  selectQuickBooksAssociations,
  selectQuickBooksCompany,
  selectQuickBooksCustomers,
} from '@sophocles/features/quickbooks/slice';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';
import quickBooksIconUrl from '@sophocles/public/qbo-icon.png';
import { billingAccountPath, billingInvoicePath } from '@sophocles/router/paths';
import { formatNumber } from '@sophocles/utils/formatUtils';
import AccountBillingNotes from '@sophocles/views/billing/components/AccountBillingNotes';
import BillingPeriodSelect from '@sophocles/views/billing/components/BillingPeriodSelect';
import BillingStatusSelect from '@sophocles/views/billing/components/BillingStatusSelect';
import InvoiceDetailTable from '@sophocles/views/billing/components/InvoiceDetailTable';

// Inner recurring/one-time panels continue the bounded height chain down to
// the grid; without it, an infinite-scroll grid gets an unbounded viewport
// and lazy loading fetches the entire result set. Scoped to :not([hidden])
// so an inactive panel keeps the hidden attribute's display:none.
const GRID_TAB_PANEL_SX = {
  p: 1,
  width: '100%',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  '&:not([hidden])': { display: 'flex', flexDirection: 'column' },
};

// Keyed by the stable InvoiceStatus code (pliny odl_billing.models.invoice),
// not the localized label, so the chip color never depends on locale.
const INVOICE_STATUS_CHIP = {
  ready: { color: 'info', labelKey: 'billing.ready', fallback: 'Ready' },
  pending: { color: 'info', labelKey: 'billing.pending', fallback: 'Pending' },
  complete: { color: 'ok', labelKey: 'billing.complete', fallback: 'Complete' },
};

function InvoiceTable({ accountId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const {
    user,
    permissions: { isQuickBooksUser },
  } = useUser();
  const account = useSelector(selectBillingAccount(accountId));
  const quickBooksCompany = useSelector(selectQuickBooksCompany);
  const quickBooksCompanies = useSelector(selectQuickBooksCustomers);
  const quickBooksAssociations = useSelector(selectQuickBooksAssociations);

  const [tab, setTab] = useState('recurring');
  const [selectedSiteGroupId, setSelectedSiteGroupId] = useState(null);
  const [status, setStatus] = useState('ready');
  const [period, setPeriod] = useState('all');
  const [isAssociationDialogOpen, setIsAssociationDialogOpen] = useState(false);
  const [tableSummary, setTableSummary] = useState({});

  const isQuickBooksSignedIn = quickBooksCompany != null;
  const isPeriodSelected = period !== 'all';
  const [periodStart, periodEnd] = period.split(' - ');

  // Stable ref to EntityGrid's refresh fn, captured via onGridReady — the
  // grid owns its own fetch state internally, so this is the only way a
  // mutation outside the grid (Mark Complete, Mark All Complete) can force a
  // page-0 refetch.
  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();

  // Drives EntityGrid's list fetch. The odl-app grid-spec parses filter
  // values as `filter[field]` (default op) or `filter[field.op]` (explicit
  // op) — start/end date need the `.gte`/`.lte` op form since the spec's
  // datetime fields default to `exact`. Must stay memoized — EntityGrid
  // recreates its dataSource (clearing the grid's cache) whenever this
  // reference changes.
  const extraParams = useMemo(() => {
    const params = { is_recurring: tab === 'recurring' };
    if (accountId != null) params.account = accountId;
    if (status !== 'all') params.status = status;
    if (isPeriodSelected) {
      params['start_date.gte'] = periodStart;
      params['end_date.lte'] = periodEnd;
    }
    return params;
  }, [accountId, status, tab, isPeriodSelected, periodStart, periodEnd]);

  // Same filter values, shaped for the legacy /billing/invoices/ endpoint the
  // summary rollup below still reads (django-filter double-underscore
  // lookups, not the grid's filter[field.op] form).
  const summaryParams = useMemo(() => {
    const params = { is_recurring: tab === 'recurring' };
    if (accountId != null) params.account = accountId;
    if (status !== 'all') params.status = status;
    if (isPeriodSelected) {
      params.start_date__gte = periodStart;
      params.end_date__lte = periodEnd;
    }
    return params;
  }, [accountId, status, tab, isPeriodSelected, periodStart, periodEnd]);

  // Per-category quantity/price rollup for the selected billing period,
  // decoupled from the grid's own paged fetch (which under infinite scroll
  // never has the full result set in hand at once). Only fetched when the
  // summary panel is actually shown.
  useEffect(() => {
    if (tab !== 'recurring' || !isPeriodSelected) {
      setTableSummary({});
      return undefined;
    }
    let cancelled = false;
    fetchInvoices({ ...summaryParams, limit: 1000, offset: 0 })
      .then(data => {
        if (cancelled) return;
        const summary = (data.results ?? []).reduce((acc, invoice) => {
          if (!invoice.stats) return acc;
          const next = { ...acc };
          Object.entries(invoice.stats).forEach(([category, values]) => {
            const existing = next[category];
            next[category] = {
              total_quantity: (existing?.total_quantity ?? 0) + parseFloat(values.total_quantity),
              total_price: (existing?.total_price ?? 0) + parseFloat(values.total_price),
            };
          });
          return next;
        }, {});
        setTableSummary(summary);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load invoice summary', error);
        setTableSummary({});
      });
    return () => {
      cancelled = true;
    };
  }, [tab, isPeriodSelected, summaryParams]);

  // Render overrides on top of the descriptor's columns (name, frequency_display,
  // start_date, end_date, total, status). Sizing/order/headers/sortability come
  // from the grid-spec; only cell rendering is supplied here.
  const fieldOverrides = useMemo(
    () => ({
      name: {
        renderCell: ({ row, value }) => {
          const matchedQuickBooksAssociation = quickBooksAssociations.find(a =>
            a.integrations?.some(
              ia =>
                ia.account_id === accountId &&
                (row.site_group == null ? ia.site_group_id == null : ia.site_group_id === row.site_group),
            ),
          );
          const isMappedInQuickBooks = Boolean(matchedQuickBooksAssociation);
          const quickBooksCustomer = matchedQuickBooksAssociation?.association;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              {value ? (
                <Link to={billingInvoicePath({ invoiceId: row.id })} onClick={e => e.stopPropagation()}>
                  {value}
                </Link>
              ) : (
                <Typography>{value}</Typography>
              )}
              {isQuickBooksUser &&
                isQuickBooksSignedIn &&
                (isMappedInQuickBooks ? (
                  <Tooltip title={quickBooksCustomer?.DisplayName}>
                    <Chip
                      label={t('billing.associated', 'Associated')}
                      color="success"
                      size="small"
                      variant="outlined"
                      onDelete={e => {
                        e.stopPropagation();
                        confirmDelete(row, {
                          entityLabel: t('billing.account.quickBooksAssociation'),
                          onConfirm: deletedRow => dispatch(disassociateQuickBooksCustomer(deletedRow.site_group)),
                        });
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    label={t('billing.associate', 'Associate')}
                    color="primary"
                    size="small"
                    variant="outlined"
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedSiteGroupId(row.site_group);
                      setIsAssociationDialogOpen(true);
                    }}
                  />
                ))}
            </Box>
          );
        },
      },
      total: {
        valueFormatter: value =>
          value == null ? '' : `$${formatNumber(Number(value) || 0, user, MONEY_FORMAT_OPTIONS)}`,
      },
      status: {
        renderCell: ({ value }) => {
          const chip = INVOICE_STATUS_CHIP[value];
          return (
            <GridStatusCell color={chip?.color ?? 'info'} label={chip ? t(chip.labelKey, chip.fallback) : value} />
          );
        },
      },
    }),
    [accountId, quickBooksAssociations, isQuickBooksUser, isQuickBooksSignedIn, confirmDelete, dispatch, t, user],
  );

  // The descriptor carries no account/customer column (this grid is always
  // account-scoped by the sole caller, AccountInvoicesView) — this stays
  // dormant until a global, unscoped invoices list exists, matching the
  // pre-conversion behavior.
  const extraColumns = useMemo(() => {
    if (accountId != null) return undefined;
    return [
      {
        field: 'customer',
        headerName: t('billing.columnAccount', 'Account'),
        sortable: true,
        flex: 1,
        renderCell: ({ value }) => {
          const linkedAccountId = value?.id ?? '';
          const name = value?.name ?? t('billing.unknown', 'Unknown');
          return value ? (
            <Link to={billingAccountPath({ accountId: linkedAccountId })} onClick={e => e.stopPropagation()}>
              {name}
            </Link>
          ) : (
            '-'
          );
        },
      },
    ];
  }, [accountId, t]);

  const handleRowClick = useCallback(params => navigate(billingInvoicePath({ invoiceId: params.row.id })), [navigate]);

  // Master-detail expansion: the toggle column MUI renders alongside the row
  // is a separate control from the row click above, so the two never collide.
  const getDetailPanelContent = useCallback(
    ({ row }) => {
      // Group label precedence: the expanded category object, then the
      // server's line_category string; a null label lands every remaining
      // item in the single uncategorized bucket (InvoiceDetailTable renders
      // a null name as "Uncategorized").
      const UNCATEGORIZED_KEY = ' uncategorized';
      const categories = {};
      row.items.forEach((item, invoiceItemIndex) => {
        const itemData = {
          id: item.id,
          index: invoiceItemIndex,
          name: item.description,
          description: item.notes,
          quantity: parseFloat(item.quantity),
          price: parseFloat(item.price),
          billed_entities_count: item.billed_entities_count,
          billed_entities_breakdown: item.billed_entities_breakdown,
        };
        const label = item.category?.name ?? item.line_category ?? null;
        const key = label ?? UNCATEGORIZED_KEY;
        if (key in categories) {
          categories[key].items.push(itemData);
        } else {
          categories[key] = {
            id: item.category?.id ?? null,
            type_id: item.category_type ?? null,
            name: label,
            items: [itemData],
          };
        }
      });

      const transformedData = Object.values(categories).map(category => ({
        id: category.id,
        type_id: category.type_id,
        name: category.name,
        items: category.items,
      }));

      return (
        <Box sx={{ p: 2, backgroundColor: 'var(--surface-secondary)' }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {row.status === 'ready' && (
              <Button
                color="success"
                variant="contained"
                onClick={() => {
                  dispatch(markInvoicesCompleteByIds([row.id]))
                    .unwrap()
                    .then(() => refreshGrid());
                }}
              >
                {t('billing.markComplete', 'Mark Complete')}
              </Button>
            )}
            {isQuickBooksSignedIn &&
              (row.foreign_id == null ? (
                <Button
                  variant="contained"
                  disabled={accountId == null}
                  onClick={() => dispatch(pushQuickBooksInvoice(row.id)).unwrap()}
                >
                  {t('billing.pushInvoice', 'Push Invoice')}
                </Button>
              ) : (
                <a href={`https://app.qbo.intuit.com/app/invoice?txnId=${row.foreign_id}`}>
                  <img
                    src={quickBooksIconUrl}
                    style={{ maxHeight: '24px' }}
                    alt={t('billing.quickbooks', 'Quickbooks')}
                  />
                </a>
              ))}
          </Stack>
          <InvoiceDetailTable
            editable={false}
            invoiceData={transformedData}
            setInvoiceData={null}
            categorySubtotals={row.category_subtotals}
            serverTotal={row.total}
            invoiceId={row.id}
          />
        </Box>
      );
    },
    [dispatch, t, isQuickBooksSignedIn, accountId, refreshGrid],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const toolbarContent = useMemo(
    () => (
      <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
        <Stack direction="row" spacing={1}>
          <BillingStatusSelect size="small" value={status} onChange={newStatus => setStatus(newStatus)} />
          {tab === 'recurring' && (
            <BillingPeriodSelect
              size="small"
              periods={account?.periods ?? []}
              value={period}
              onChange={newPeriod => setPeriod(newPeriod)}
            />
          )}
        </Stack>
        <Tooltip
          title={
            isPeriodSelected && status === 'ready'
              ? t('billing.markAllReadyComplete', 'Mark all ready invoices complete.')
              : t('billing.selectStatusPeriodHint', 'Select a status of Ready and a period to enable this action.')
          }
        >
          <span>
            <Button
              size="small"
              disabled={!isPeriodSelected || status !== 'ready'}
              onClick={() => {
                dispatch(markInvoicesCompleteByAccountId({ accountId, startDate: periodStart, endDate: periodEnd }))
                  .unwrap()
                  .then(() => refreshGrid());
              }}
            >
              {t('billing.markAllComplete', 'Mark All Complete')}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    ),
    [status, tab, account, period, isPeriodSelected, accountId, periodStart, periodEnd, dispatch, t, refreshGrid],
  );

  // Same grid instance rendered from whichever tab is active — TabPanel
  // unmounts the inactive one, so only one copy is ever mounted at a time.
  const gridElement = (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
      <EntityGrid
        basePath="billing-invoices"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          onRowClick: handleRowClick,
          toolbarContent,
          toolbarVariant: 'flat',
          searchPlaceholder: t('billing.searchInvoices', 'Search invoices'),
          countNoun: t('billing.invoicesCountNoun', 'invoices'),
          infiniteScroll: true,
          persistKey: 'billingInvoicesList',
          defaultSort: [{ field: 'start_date', sort: 'desc' }],
          extraColumns,
          onGridReady: handleGridReady,
          gridProps: {
            clickableRows: true,
            getDetailPanelContent,
            getDetailPanelHeight,
          },
        }}
      />
    </Box>
  );

  return (
    <>
      {deleteConfirmDialog}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
        <TabContext value={tab}>
          <Tabs variant="fullWidth" value={tab} onChange={(e, newTab) => setTab(newTab)} sx={{ flexShrink: 0 }}>
            <Tab label={t('billing.recurring', 'Recurring')} value="recurring" />
            <Tab label={t('billing.oneTime', 'One Time')} value="orders" />
          </Tabs>
          <TabPanel value="recurring" sx={GRID_TAB_PANEL_SX}>
            {isPeriodSelected && (
              <Paper sx={{ mx: 0, p: 2, width: '100%', flexShrink: 0 }}>
                <Typography variant="h5">{t('billing.summary', 'Summary')}</Typography>
                {Object.keys(tableSummary).map(category => (
                  <Typography key={category}>
                    {category}: {tableSummary[category].total_quantity}
                  </Typography>
                ))}
              </Paper>
            )}
            {gridElement}
          </TabPanel>
          <TabPanel value="orders" sx={GRID_TAB_PANEL_SX}>
            {gridElement}
          </TabPanel>
        </TabContext>
      </Box>
      {isQuickBooksUser && (
        <Formik
          enableReinitialize
          initialValues={{ companyId: null }}
          onSubmit={values => {
            dispatch(
              createQuickBooksAssociation({
                quickBooksCustomerId: parseInt(values.companyId?.value),
                accountId: accountId,
                siteGroupId: selectedSiteGroupId,
              }),
            ).finally(() => {
              setIsAssociationDialogOpen(false);
            });
          }}
        >
          {({ values, handleChange, submitForm, resetForm, isSubmitting, touched, errors, dirty, isValid }) => (
            <ResponsiveDialog
              title={t('billing.associateSiteGroup', 'Associate Site Group')}
              open={isAssociationDialogOpen}
              submitText={t('billing.associate', 'Associate')}
              canSubmit={values.companyId != null}
              onSubmit={submitForm}
              onClose={() => setIsAssociationDialogOpen(false)}
            >
              <Form>
                <Field name="companyId">
                  {({ field, form, meta }) => (
                    <Autocomplete
                      {...field}
                      options={quickBooksCompanies.map(c => ({ label: c.DisplayName, value: c.Id }))}
                      getOptionLabel={option => option?.label ?? ''}
                      onChange={(event, newValue) => form.setFieldValue(field.name, newValue)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t('billing.quickbooksCompany', 'Quickbooks Company')}
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error ? meta.error : ''}
                        />
                      )}
                    />
                  )}
                </Field>
              </Form>
            </ResponsiveDialog>
          )}
        </Formik>
      )}
    </>
  );
}

export default InvoiceTable;
